import { useState, useEffect, useRef } from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { IoCloseCircle } from "react-icons/io5";
import API from "@src/API.js";
import UserData from "../Controls/UserData";
import CustomButton from "../Controls/CustomButton";
import CmModal from "../Controls/CmModal";
import EmployerListItem from "../ListItems/EmployerListItem";
import EmployerEditModal from "./EmployerEditModal";
import "../../style/scss/modal.scss";
import "../../style/scss/FormActions.scss";

function employerId(e) {
  return Number(e?.ID ?? e?.id ?? 0) || 0;
}

function employerName(e) {
  return e?.Name ?? e?.name ?? "";
}

/** Open same-origin route in a new tab without stripping sessionStorage (auth). */
function openAppTab(path) {
  // Avoid <a target="_blank" rel="noreferrer"> — new tab gets empty sessionStorage
  // and App treats the user as logged out.
  window.open(path, "_blank");
}

export default function EmployerSelect({
  label = "Employer",
  value,
  onSelect,
  className,
  hidden,
  clearable = false,
  invalid = false,
  /** When true, only the search modal is rendered (parent owns the display UI). */
  hideTrigger = false,
  /** Controlled open state for the picker modal (optional). */
  open: openProp,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const showModal = openProp !== undefined ? !!openProp : internalOpen;

  function setShowModal(next) {
    if (openProp === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  }

  const [searchStr, setSearchStr] = useState("");
  const [employers, setEmployers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editEmployerId, setEditEmployerId] = useState(null);
  const [promptForAdmin, setPromptForAdmin] = useState(false);
  const abortControllerRef = useRef(null);

  const selectedId = employerId(value);
  const selectedName = employerName(value);

  useEffect(() => {
    if (!showModal) return;
    setSearchStr(selectedName);
  }, [showModal, selectedName]);

  useEffect(() => {
    if (!showModal) return undefined;

    const name = (searchStr || "").trim();
    if (name.length < 3) {
      setEmployers([]);
      setSearching(false);
      return undefined;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let cancelled = false;
    (async () => {
      setSearching(true);
      try {
        const resp = await API.getAsync(`/EmployerSearch?Name=${encodeURIComponent(name)}`, null, {
          signal: controller.signal,
        });
        if (cancelled || controller.signal.aborted) return;
        if (resp?.ok) {
          setEmployers(resp.jsonData?.companies || []);
        } else {
          setEmployers([]);
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Employer search error:", error);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [searchStr, showModal]);

  function closeModal() {
    setShowModal(false);
    setEmployers([]);
    setSearchStr("");
  }

  function openModal() {
    setShowModal(true);
  }

  function pickEmployer(company) {
    onSelect?.(company);
    closeModal();
  }

  async function createEmployer() {
    const name = (searchStr || "").trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const userData = UserData.UserData() || {};
      if (!Array.isArray(userData.employers)) {
        userData.employers = [];
        window.sessionStorage.setItem("UserData", JSON.stringify(userData));
      }
      const payload = {
        ID: 0,
        Name: name,
        ShortName: name.slice(0, 20),
        Description: "",
      };
      const resp = await UserData.UpdateAsync("employers", payload, true);
      if (!resp?.ok) {
        console.error("Could not create employer", resp);
        return;
      }
      const id =
        Number(payload.ID) ||
        Number(resp.saveResp?.jsonData?.id) ||
        Number(resp.saveResp?.jsonData?.ID) ||
        0;
      pickEmployer({ ID: id, Name: name, ShortName: payload.ShortName });
      if (id > 0) {
        setPromptForAdmin(true);
        setEditEmployerId(id);
      }
    } finally {
      setCreating(false);
    }
  }

  const exactMatch = employers.some(
    (c) => employerName(c).toLowerCase() === (searchStr || "").trim().toLowerCase()
  );
  const canCreate = (searchStr || "").trim().length >= 2 && !exactMatch;
  const trimmedSearch = (searchStr || "").trim();

  return (
    <>
      {!hideTrigger ? (
        <div
          className={`field EmployerSelect form-group${invalid ? " field-invalid" : ""}${className ? ` ${className}` : ""}`}
          hidden={hidden || undefined}
        >
          <label className="form-label">{label}</label>
          <div
            className={`form-input employer-select-trigger${invalid ? " field-invalid-control" : ""}`}
            role="button"
            tabIndex={0}
            onClick={openModal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openModal();
              }
            }}
            data-testid="employer-select-trigger"
          >
            <span className={selectedName ? "" : "employer-select-placeholder"}>
              {selectedName || "Select employer…"}
            </span>
            {selectedId > 0 ? (
              <span className="actions">
                <button
                  type="button"
                  className="employer-select-action"
                  title="View employer"
                  aria-label="View employer"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAppTab(`/employerView/${selectedId}`);
                  }}
                >
                  <FaExternalLinkAlt aria-hidden />
                </button>
                {clearable ? (
                  <button
                    type="button"
                    className="employer-select-action"
                    title="Clear"
                    aria-label="Clear employer"
                    data-testid="employer-select-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect?.(null);
                    }}
                  >
                    <IoCloseCircle aria-hidden />
                  </button>
                ) : null}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <CmModal
        open={showModal}
        onClose={closeModal}
        title="Select Employer"
        size="wide"
        testId="employer-select-modal"
        className="employerSelect"
        footer={
          canCreate ? (
            <CustomButton
              type="button"
              variant="contained"
              color="primary"
              data-testid="employer-select-create"
              disabled={creating}
              onClick={createEmployer}
            >
              {creating ? "Creating…" : `Create “${trimmedSearch}”`}
            </CustomButton>
          ) : null
        }
      >
        <label htmlFor="employer-select-search" className="form-label">
          Employer
        </label>
        <input
          id="employer-select-search"
          className="form-input"
          data-testid="employer-select-search"
          value={searchStr}
          onChange={(e) => setSearchStr(e.target.value)}
          placeholder="Type at least 3 characters to search"
          autoFocus
        />
        <p className="byline employer-select-hint">
          Click a company to select it. Use the icons to view or edit employer details.
        </p>
        {searching ? <p className="byline">Searching…</p> : null}
        {!searching && trimmedSearch.length >= 3 && employers.length === 0 ? (
          <p className="byline">No employers matched “{trimmedSearch}”.</p>
        ) : null}
        <ul className="employer-select-results" data-testid="employer-select-results">
          {employers.map((company) => (
            <EmployerListItem
              key={employerId(company) || employerName(company)}
              employer={company}
              onSelect={pickEmployer}
              onEdit={(id) => {
                setPromptForAdmin(false);
                setEditEmployerId(id);
              }}
            />
          ))}
        </ul>
      </CmModal>
      {editEmployerId != null ? (
        <EmployerEditModal
          employerId={editEmployerId}
          promptForAdmin={promptForAdmin}
          onClose={() => {
            setEditEmployerId(null);
            setPromptForAdmin(false);
          }}
        />
      ) : null}
    </>
  );
}
